import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { isAxiosError } from 'axios'
import { getUserInfo } from '@/utils/api'
import { promiseWithTimeout } from '@/helpers'
import type { GameGlassUser } from '@/types/api'

// TODO: aggregate all queries together
export const CURRENT_USER_QUERY_KEY = 'currentUser' as const

export type { GameGlassUser }

// The backend assigns `username` when the user first signs in through GameGlass
// Hub, so its absence means Hub has not been set up yet.
export const hasOnboarded = (user?: Pick<GameGlassUser, 'username'>): boolean => Boolean(user?.username)

// The entire `_auth` subtree gates on `user.token`, so a 200 response without
// one is a failure, not a value. Resolving it would park the app on a spinner
// with nothing logged — the silent hang this hook exists to avoid.
export class MissingUserTokenError extends Error {
  constructor() {
    super('Your profile loaded without an auth token.')
    this.name = 'MissingUserTokenError'
  }
}

// Axios' own `timeout` (20s) only covers the HTTP phase, which starts after the
// request interceptor resolves — and that interceptor awaits Clerk's getToken().
// If Clerk stalls, the promise never settles and the query sits in `pending`
// forever with no error to retry or report. Sits just above the axios timeout so
// it stays a backstop for that case rather than pre-empting the normal one.
const REQUEST_TIMEOUT = 25_000

// Matches TanStack's `retry: 5` semantics — it retries while failureCount < 5,
// so five attempts in total before the query settles into `isError`.
const MAX_ATTEMPTS = 5

// A 4xx will never succeed by repeating it — the session needs re-authenticating.
// Failing fast puts the retry/logout actions in front of the user immediately
// instead of after a full backoff cycle. Everything else (network failure,
// timeout, 5xx, 408, 429) is transient and worth the retries.
export const isTerminalAuthError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  if (status === undefined) {
    return false
  }
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

export const useCurrentUser = () => {
  const { isSignedIn } = useAuth()

  return useQuery<GameGlassUser>({
    queryKey: [CURRENT_USER_QUERY_KEY, isSignedIn],
    queryFn: async () => {
      const { data } = await promiseWithTimeout(getUserInfo(), REQUEST_TIMEOUT, {
        url: 'users/@me',
      })
      if (!data?.token) {
        throw new MissingUserTokenError()
      }
      return data
    },
    // Clerk reports `undefined` while loading — only fetch once it confirms a session
    enabled: !!isSignedIn,
    staleTime: 10 * 60 * 1000, // 10 minutes
    // Ride out transient failures, then stop and let the caller surface the
    // error with retry/logout actions — an unbounded spinner is the bug this
    // hook exists to prevent, and so is an unbounded retry loop behind one.
    retry: (failureCount, error) => !isTerminalAuthError(error) && failureCount < MAX_ATTEMPTS,
    retryDelay: (failureCount) => Math.min(1_000 * 2 ** failureCount, 30_000),
  })
}
